import { useCallback, useState } from "react";
import { adminApiService } from "../utils/api";
import { useAuthHeaders } from "./useAuthHeaders";
import { GfImage, UpdateGfImageDto } from "@swagger/typescript-fetch-goflyer";

/**
 * Hook providing update and delete operations for GfImage.
 * Uses adminApiService under the hood and returns loading/error state.
 */
export const useGfImage = () => {
  const authHeaders = useAuthHeaders();

  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const updateImage = useCallback(
    async (payload: UpdateGfImageDto): Promise<GfImage> => {
      if (!payload?.id) {
        throw new Error("updateImage requires payload.id");
      }
      setUpdating(true);
      setError(null);
      try {
        // Pass auth headers as options to be consistent with secured endpoints
        return await adminApiService.updateOneBaseGfImageAdminControllerGfImage(
          payload,
          payload.id as string,
          authHeaders
        );
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setUpdating(false);
      }
    },
    [authHeaders]
  );

  /**
   * Soft delete: sets isDeleted = true. The row and the S3 files are kept, and
   * the image can be brought back from the Images page's "Deleted" view
   * (POST /admin-image/restore).
   * Returns an array because the endpoint takes a list of ids.
   */
  const deleteImage = useCallback(
    async (id: string): Promise<GfImage[]> => {
      if (!id) throw new Error("deleteImage requires an id");
      setDeleting(true);
      setError(null);
      try {
        return await adminApiService.gfImageAdminControllerSoftDelete(
          { ids: [id] },
          authHeaders
        );
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setDeleting(false);
      }
    },
    [authHeaders]
  );

  return {
    updateImage,
    deleteImage,
    updating,
    deleting,
    error,
    loading: updating || deleting,
  };
};
